<script setup lang="ts">
import type { FieldProps } from './interface'
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import AutoFormLabel from './AutoFormLabel.vue'
import { beautifyObjectName } from './utils'
import { computed } from 'vue'

const props = defineProps<FieldProps & {
  options?: string[]
}>()

/** Display label for an option: use selectOptions when provided, else beautify raw value */
const getOptionLabel = (value: string) => {
  const found = props.config?.selectOptions?.find((o) => o.value === value)
  return found ? found.label : beautifyObjectName(value)
}
const optionsWithLabels = computed(() =>
  (props.options ?? []).map((value) => ({ value, label: getOptionLabel(value) }))
)
</script>

<template>
  <FormField v-slot="slotProps" :name="fieldName">
    <FormItem>
      <AutoFormLabel v-if="!config?.hideLabel" :required="required">
        {{ config?.label || beautifyObjectName(label ?? fieldName) }}
      </AutoFormLabel>
      <FormControl>
        <slot v-bind="slotProps">
          <RadioGroup v-if="config?.component === 'radio'" :disabled="disabled" :orientation="'vertical'" v-bind="{ ...slotProps.componentField }">
            <div v-for="(item, index) in optionsWithLabels" :key="item.value" class="mb-2 flex items-center gap-3 space-y-0">
              <RadioGroupItem :id="`${item.value}-${index}`" :value="item.value" />
              <Label :for="`${item.value}-${index}`">{{ item.label }}</Label>
            </div>
          </RadioGroup>

          <Select v-else :disabled="disabled" v-bind="{ ...slotProps.componentField }">
            <SelectTrigger class="w-full">
              <SelectValue :placeholder="config?.inputProps?.placeholder" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="item in optionsWithLabels" :key="item.value" :value="item.value">
                {{ item.label }}
              </SelectItem>
            </SelectContent>
          </Select>
        </slot>
      </FormControl>

      <FormDescription v-if="config?.description">
        {{ config.description }}
      </FormDescription>
      <FormMessage />
    </FormItem>
  </FormField>
</template>
